import type { UserType } from '../../graphql/generated/graphql';
import type {
  FuzzySearchMatch,
  FuzzySearchResult,
} from '../../utils/search/fuzzySearch';
import fuzzySearch, { FuzzySearchIndex } from '../../utils/search/fuzzySearch';

export type LocalEntitySearchType = 'group' | 'user' | 'topic' | 'post';

type BaseData = { id: string };
type GroupData = BaseData & { name: string };
type UserData = BaseData & { name: string; type?: UserType | null };
type TopicData = BaseData & {
  name: string;
  score?: number | null;
  ancestors?: Omit<TopicData, 'ancestors'>[];
};

type PostData = BaseData & {
  archivedAt?: string | null;
  title: string;
  topics?: TopicData[];
};

type BaseEntity<Data extends BaseData, T extends LocalEntitySearchType> = {
  id: string;
  name: string;
  score: number;
  matches: FuzzySearchMatch[];
  secondary?: React.ReactNode;
} & { type: T; object: Data } & { [key in T]: Data };

export type GroupEntity<G extends GroupData = GroupData> = BaseEntity<
  G,
  'group'
>;
export type PostEntity<P extends PostData = PostData> = BaseEntity<P, 'post'>;
export type TopicEntity<T extends TopicData = TopicData> = BaseEntity<
  T,
  'topic'
>;
export type UserEntity<U extends UserData = UserData> = BaseEntity<U, 'user'>;

export type BaseSearchConfig<D extends BaseData> = {
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  extra?: (obj: D) => any;
  filter?: (obj: D) => boolean;
  objects: D[];
  sortBy?: (l: D, r: D) => number;
};

export type LocalEntitySearchConfig<
  G extends GroupData = GroupData,
  P extends PostData = PostData,
  T extends TopicData = TopicData,
  U extends UserData = UserData,
> = {
  group?: BaseSearchConfig<G>;
  post?: BaseSearchConfig<P>;
  topic?: BaseSearchConfig<T>;
  user?: BaseSearchConfig<U> & { includeDeactivated?: boolean };
  order?: LocalEntitySearchType[];
};

type ToEntity<Data extends BaseData, T extends LocalEntitySearchType> = (
  object: Data,
  options?: {
    score?: number;
    matches?: FuzzySearchMatch[];
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    extra?: (obj: Data) => any;
  },
) => BaseEntity<Data, T>;

// Use consistent field weights across types to normalize scores
enum FIELD_WEIGHT {
  Main = 1,
  Secondary = 0.2,
  Tertiary = 0.1,
}

type ToEntityOptions<
  Data extends BaseData,
  ExtraData extends object = Record<string, unknown>,
> = {
  score?: number;
  matches?: FuzzySearchMatch[];
  extra?: (obj: Data) => ExtraData | undefined;
};

const baseToEntity = <
  Data extends BaseData & { [name in NameKey]: string },
  T extends LocalEntitySearchType,
  NameKey extends string,
  ExtraData extends object,
>(
  type: T,
  nameKey: keyof Data,
  object: Data,
  {
    score = 0,
    matches = [],
    extra = () => undefined,
  }: ToEntityOptions<Data, ExtraData> = {},
): BaseEntity<Data, T> & ExtraData => ({
  ...({ [type]: object } as { [key in T]: Data }),
  ...(extra(object) as ExtraData),
  id: object.id,
  name: object[nameKey],
  type,
  object,
  score,
  matches,
});

export const groupToEntity = <G extends Pick<GroupData, 'id' | 'name'>>(
  group: G,
  options?: ToEntityOptions<G>,
) => baseToEntity('group', 'name', group, options);
export const postToEntity = <P extends Pick<PostData, 'id' | 'title'>>(
  post: P,
  options?: ToEntityOptions<P>,
) => baseToEntity('post', 'title', post, options);
export const topicToEntity = <T extends Pick<TopicData, 'id' | 'name'>>(
  topic: T,
  options?: ToEntityOptions<T>,
) => baseToEntity('topic', 'name', topic, options);
export const userToEntity = <U extends Pick<UserData, 'id' | 'name'>>(
  user: U,
  options?: ToEntityOptions<U>,
) => baseToEntity('user', 'name', user, options);

const BYPASS_SORT = () => 0;

const cachedFuzzySearchIndex = new WeakMap<
  BaseData[],
  FuzzySearchIndex<unknown, string>
>();

const getFuzzySearchIndex = <Data extends BaseData, K extends string>(
  objects: Data[],
  keys: K[],
): FuzzySearchIndex<Data, K> => {
  const cached = cachedFuzzySearchIndex.get(objects);
  if (cached) {
    return cached as FuzzySearchIndex<Data, K>;
  }

  const index = new FuzzySearchIndex(objects, keys);
  cachedFuzzySearchIndex.set(objects, index);
  return index;
};

const singleEntitySearch = <
  Data extends BaseData,
  T extends LocalEntitySearchType,
  K extends string,
>(
  query: string,
  keys: K[],
  { extra, filter, objects, sortBy = BYPASS_SORT }: BaseSearchConfig<Data>,
  {
    tiebreaker,
    toEntity,
    weights,
  }: {
    tiebreaker: (a: Data, b: Data) => number;
    toEntity: ToEntity<Data, T>;
    weights: Record<K, number>;
  },
) => {
  const searchResults: FuzzySearchResult<Data>[] = query.length
    ? fuzzySearch<Data, K>(query, getFuzzySearchIndex(objects, keys), {
        weights,
        filter,
      })
    : (filter ? objects.filter(filter) : objects).map((obj, index) => ({
        item: obj,
        matches: [],
        score: 0,
        refIndex: index,
      }));

  return searchResults
    .sort(({ item: l }, { item: r }) => sortBy(l, r) || tiebreaker(l, r))
    .map(({ item, matches, score }) =>
      toEntity(item, { score, matches, extra }),
    );
};

export enum GroupMatchKey {
  Name = 'name',
}
const GROUP_INDEX_KEYS = [GroupMatchKey.Name];

const groupSearch = <G extends GroupData>(
  query: string,
  space: BaseSearchConfig<G>,
) => {
  const tiebreaker = (g1: G, g2: G) =>
    g1.name.toUpperCase().localeCompare(g2.name.toUpperCase());

  return singleEntitySearch(query, GROUP_INDEX_KEYS, space, {
    tiebreaker,
    toEntity: groupToEntity,
    weights: {
      [GroupMatchKey.Name]: FIELD_WEIGHT.Main,
    },
  });
};

export enum PostMatchKey {
  Title = 'title',
  TopicNames = 'topics.name',
  AncestorTopicNames = 'topics.ancestors.name',
}
const POST_INDEX_KEYS = [
  PostMatchKey.Title,
  PostMatchKey.TopicNames,
  PostMatchKey.AncestorTopicNames,
];

const postSearch = <P extends PostData>(
  query: string,
  space: BaseSearchConfig<P>,
) => {
  const tiebreaker = (p1: P, p2: P) => {
    if (p1.archivedAt && p2.archivedAt) {
      return (
        new Date(p2.archivedAt).getTime() - new Date(p1.archivedAt).getTime()
      );
    }
    return p1.archivedAt ? 1 : -1;
  };

  return singleEntitySearch(query, POST_INDEX_KEYS, space, {
    tiebreaker,
    toEntity: postToEntity,
    weights: {
      [PostMatchKey.Title]: FIELD_WEIGHT.Main,
      [PostMatchKey.TopicNames]: FIELD_WEIGHT.Secondary,
      [PostMatchKey.AncestorTopicNames]: FIELD_WEIGHT.Tertiary,
    },
  });
};

export enum TopicMatchKey {
  Name = 'name',
  AncestorNames = 'ancestors.name',
}
const TOPIC_INDEX_KEYS = [TopicMatchKey.Name, TopicMatchKey.AncestorNames];

const topicSearch = <T extends TopicData>(
  query: string,
  space: BaseSearchConfig<T>,
) => {
  const tiebreaker = (t1: T, t2: T) => (t2.score || 0) - (t1.score || 0);

  return singleEntitySearch(query, TOPIC_INDEX_KEYS, space, {
    tiebreaker,
    toEntity: topicToEntity,
    weights: {
      [TopicMatchKey.Name]: FIELD_WEIGHT.Main,
      [TopicMatchKey.AncestorNames]: FIELD_WEIGHT.Secondary,
    },
  });
};

export enum UserMatchKey {
  Name = 'name',
  Email = 'email',
  Title = 'title',
}
const USER_INDEX_KEYS = [
  UserMatchKey.Name,
  UserMatchKey.Title,
  UserMatchKey.Email,
];

const userSearch = <U extends UserData>(
  query: string,
  space: BaseSearchConfig<U>,
) => {
  const tiebreaker = (u1: U, u2: U) =>
    u1.name.toUpperCase().localeCompare(u2.name.toUpperCase());

  const isSearchingEmail = !query.match(/\s/) && query.includes('@');
  return singleEntitySearch(query, USER_INDEX_KEYS, space, {
    tiebreaker,
    toEntity: userToEntity,
    weights: isSearchingEmail
      ? {
          [UserMatchKey.Name]: 0,
          [UserMatchKey.Title]: 0,
          [UserMatchKey.Email]: FIELD_WEIGHT.Main,
        }
      : {
          [UserMatchKey.Name]: FIELD_WEIGHT.Main,
          [UserMatchKey.Title]: FIELD_WEIGHT.Secondary,
          [UserMatchKey.Email]: FIELD_WEIGHT.Tertiary,
        },
  });
};

function localEntitySearch<
  G extends GroupData = GroupData,
  P extends PostData = PostData,
  T extends TopicData = TopicData,
  U extends UserData = UserData,
>(
  query: string,
  config: LocalEntitySearchConfig<G, P, T, U>,
  options?:
    | { limit: number; limitPerType?: never }
    | { limit?: never; limitPerType: number },
): (GroupEntity<G> | PostEntity<P> | TopicEntity<T> | UserEntity<U>)[] {
  const limit = options?.limit ?? 100;
  const limitPerType = options?.limitPerType;

  const { order: specifiedOrder, ...entityConfigs } = config;
  const order =
    specifiedOrder || (Object.keys(entityConfigs) as LocalEntitySearchType[]);

  const byScore = (
    { score: s1 }: { score: number },
    { score: s2 }: { score: number },
  ) => s2 - s1;

  const results = order
    .map((type) => {
      if (!entityConfigs[type]) {
        return [];
      }

      switch (type) {
        case 'group':
          // TODO: Fix this the next time the file is edited.
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          return groupSearch<G>(query, entityConfigs[type]!);
        case 'post':
          // TODO: Fix this the next time the file is edited.
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          return postSearch<P>(query, entityConfigs[type]!);
        case 'topic':
          // TODO: Fix this the next time the file is edited.
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          return topicSearch<T>(query, entityConfigs[type]!);
        case 'user':
          // TODO: Fix this the next time the file is edited.
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          return userSearch<U>(query, entityConfigs[type]!);
        default:
          return [];
      }
    })
    .map((typeResults) =>
      limitPerType
        ? typeResults.sort(byScore).slice(0, limitPerType)
        : typeResults,
    )
    .flat();

  const sortedResults = query ? results.sort(byScore) : results;
  return limitPerType ? sortedResults : sortedResults.slice(0, limit);
}

// When working with nested arrays, `refIndex` represents the index of an item
// in the flatten array.
// For example, given `[{ ancestors: ["A", "B"] }, { ancestors: ["C"] }]`,
// the `refIndex` of `"C"` is 2.
// This function is used to get the innerIndex (0 in the example) and the outerIndex (1 in the example)
// of a given `refIndex`.
export function unflattenRefIndex<
  K extends string,
  T extends { [key in K]?: unknown[] },
>(
  items: T[],
  field: K,
  refIndex: number,
): { innerIndex: number; outerIndex: number } {
  let innerIndex = refIndex;
  const outerIndex = items.findIndex((item) => {
    const innerLength = item[field]?.length ?? 0;

    if (innerIndex < innerLength) {
      return true;
    }

    innerIndex -= innerLength;
    return false;
  });

  return { innerIndex, outerIndex };
}

export default localEntitySearch;
